import React from 'react';
import HorizontalCategoryChart from '../charts/HorizontalCategoryChart';
import BiomarkerDetailChart from '../charts/BiomarkerDetailChart';
import SectionContainer from '../common/SectionContainer';
import {HEALTH_CATEGORIES, getCategoryStats} from '@/utils/healthCategories';
import {useAppSelector} from '@/hooks/redux';
import {selectTests} from '@/slices/testSlice';
import {useDashboardData} from '@/hooks/useDashboardData';

interface HealthOverviewProps {
    selectedCategory: string;
    onCategoryChange: (category: string) => void;
    onBiomarkerSelect?: (biomarkerName: string) => void;
    selectedBiomarkerName?: string | null;
}

const HealthOverview: React.FC<HealthOverviewProps> = ({
    selectedCategory,
    onCategoryChange,
    onBiomarkerSelect,
    selectedBiomarkerName,
}) => {
    const testsState = useAppSelector(selectTests);

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: currentPatientData} = useDashboardData();

    const isLoading = testsState.loading;

    // Get all results for filtering
    const allResults = React.useMemo(() => {
        return currentPatientData.flatMap(
            (test: any) =>
                test.results?.map((result: any) => ({
                    ...result,
                    createdDate: test.createdDate,
                })) || []
        );
    }, [currentPatientData]);

    // Get categories that have available tests - sorted by score descending to match chart order
    const availableCategories = React.useMemo(() => {
        const categoryStats = getCategoryStats(allResults);
        return categoryStats
            .filter((category) => category.availableTests > 0)
            .sort((a, b) => Math.round(b.averageValue) - Math.round(a.averageValue)) // Sort by score descending
            .map((category) => [category.key, HEALTH_CATEGORIES[category.key]] as const);
    }, [allResults]);

    const currentCategoryIndex = availableCategories.findIndex(([key]) => key === selectedCategory);
    const currentCategory = HEALTH_CATEGORIES[selectedCategory];

    const handlePrevCategory = () => {
        if (availableCategories.length === 0) return;
        const prevIndex = currentCategoryIndex > 0 ? currentCategoryIndex - 1 : availableCategories.length - 1;
        onCategoryChange(availableCategories[prevIndex][0]);
    };

    const handleNextCategory = () => {
        if (availableCategories.length === 0) return;
        const nextIndex = currentCategoryIndex < availableCategories.length - 1 ? currentCategoryIndex + 1 : 0;
        onCategoryChange(availableCategories[nextIndex][0]);
    };

    return (
        <SectionContainer title="Health Overview">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                {/* Left Panel: Category List */}
                <div className="lg:col-span-2 bg-white rounded-lg border border-mld-panel-border overflow-hidden">
                    <div className="text-center py-2.5 border-b border-mld-panel-border">
                        <h3 className="text-[27px] text-blue-950">By Category</h3>
                    </div>
                    <div className="max-h-61 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <HorizontalCategoryChart
                            selectedCategory={selectedCategory}
                            onCategorySelect={onCategoryChange}
                        />
                    </div>
                </div>

                {/* Right Panel: Selected Category Biomarkers with Inline Trends */}
                <div className="lg:col-span-3 bg-white rounded-lg border border-mld-panel-border overflow-hidden">
                    {/* Category Header with Navigation */}
                    <div className="flex items-center justify-center py-2.5 border-b border-mld-panel-border relative">
                        <button
                            onClick={handlePrevCategory}
                            className="absolute left-4 w-5 h-5 bg-mld-panel-bg rounded-full flex items-center justify-center text-mld-card-text hover:text-mld-primary transition-colors border border-mld-panel-border">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                        <h3 className="text-[27px] text-blue-950">
                            {isLoading ? 'Loading...' : currentCategory?.name || 'Health Category'}
                        </h3>
                        <button
                            onClick={handleNextCategory}
                            className="absolute right-4 w-5 h-5 bg-mld-panel-bg rounded-full flex items-center justify-center text-mld-card-text hover:text-mld-primary transition-colors border border-mld-panel-border">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    </div>

                    {/* Biomarker Details with Inline Trend Charts */}
                    <div className="max-h-61 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                        <BiomarkerDetailChart
                            selectedCategory={selectedCategory}
                            onBiomarkerSelect={onBiomarkerSelect}
                            selectedBiomarkerName={selectedBiomarkerName}
                        />
                    </div>
                </div>
            </div>
        </SectionContainer>
    );
};

export default HealthOverview;
